/**
 * ChatRow.js — single chat list row.
 *
 * Pure presentational component. Click handler is passed in.
 *
 * Shows (from Phase 2.11):
 *   - Channel color pill + 2-letter prefix badge (left edge of avatar) so
 *     multi-channel accounts can scan the list fast
 *   - Session-window status dot (green = open, red = closed) computed
 *     from chat.lastInboundTs — visible even before opening the thread
 */

import { html } from 'htm/preact';
import { useStore } from '@nanostores/preact';

import { getChannelColor, getChannelLabel, getChannelInfo, $channelMeta } from '../services/channel-colors.js';
import { sessionKind } from '../shared/session-window.js';

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
  if (isNaN(d)) return '';
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)   return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getInitials(name) {
  const parts = (name || '?').trim().split(' ');
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

export function ChatRow({ chat, onClick }) {
  // Subscribe to the channel meta store so dashboard color edits re-render
  // chat rows live (or after the next /channel-meta hydrate).
  useStore($channelMeta);

  const isUnassigned = !chat.agent;
  // #154 — chat.channelPhoneId holds the Partner Panel channel_id (a UUID
  // from inbox_messages.channel_id, per #134). getChannelColor +
  // getChannelLabel now normalize that to the Meta phone_number_id before
  // looking up $channelMeta, so the left stripe + in-row chip reflect the
  // color and label the primary user set in the end-user dashboard.
  const channelColor = chat.channelPhoneId ? getChannelColor(chat.channelPhoneId) : null;
  const channelLabel = chat.channelPhoneId ? getChannelLabel(chat.channelPhoneId) : null;
  const channelInfo  = chat.channelPhoneId ? getChannelInfo(chat.channelPhoneId) : null;
  // Chip text priority: owner-set label (e.g. "Sales") > channel display_name
  // (e.g. "Odiv AI Automations") > nothing. The chip is hidden when the row
  // is an orphan (channelPhoneId null — no inbox_messages history).
  const channelChipText = channelLabel || channelInfo?.display_name || null;
  const sessionKindValue = sessionKind(chat.lastInboundTs);

  const avatar = chat.profilePic
    ? html`<img src=${chat.profilePic} alt="" loading="lazy" />`
    : getInitials(chat.name);

  return html`
    <div
      class="inbox-chat-row"
      data-chat-id=${chat.id}
      onClick=${() => onClick(chat)}
      style="position:relative;${channelColor ? `box-shadow:inset 3px 0 0 0 ${channelColor};` : ''}"
    >
      <div class="inbox-chat-avatar">${avatar}</div>
      <div class="inbox-chat-meta">
        <div class="inbox-chat-top">
          <span class="inbox-chat-name" style="display:flex;align-items:center;gap:6px;min-width:0">
            ${sessionKindValue !== 'unknown' && html`
              <span
                class="inbox-session-dot"
                style="flex-shrink:0;width:7px;height:7px;border-radius:50%;background:${sessionKindValue === 'open' ? '#25D366' : '#ef4444'};box-shadow:0 0 0 1.5px ${sessionKindValue === 'open' ? 'rgba(37,211,102,0.25)' : 'rgba(239,68,68,0.25)'}"
                title=${sessionKindValue === 'open' ? 'Session window open' : 'Session window closed — wait for the customer to message back'}
              ></span>
            `}
            <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${chat.name}</span>
          </span>
          <span class="inbox-chat-time">${formatTime(chat.lastSeen || chat.lastMessageTs)}</span>
        </div>
        <div class="inbox-chat-preview">${chat.lastMessage || ''}</div>
        ${(channelChipText || chat.unread > 0 || isUnassigned || chat.botActive || sessionKindValue === 'closed') && html`
          <div class="inbox-chat-badges" style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
            ${channelChipText && channelColor && html`
              <span
                class="inbox-badge inbox-badge-channel"
                title=${channelLabel && channelInfo?.display_name ? `${channelLabel} · ${channelInfo.display_name}` : channelChipText}
                style="display:inline-flex;align-items:center;gap:5px;background:${channelColor}22;border:1px solid ${channelColor}66;color:${channelColor};border-radius:999px;padding:2px 9px 2px 7px;font-size:0.66rem;font-weight:600;line-height:1.2;max-width:160px"
              >
                <span style="flex-shrink:0;width:6px;height:6px;border-radius:50%;background:${channelColor}"></span>
                <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${channelChipText}</span>
              </span>
            `}
            ${chat.unread > 0 && html`<span class="inbox-badge inbox-badge-unread">${chat.unread}</span>`}
            ${isUnassigned && html`<span class="inbox-badge inbox-badge-unassigned">Unassigned</span>`}
            ${chat.botActive && html`<span class="inbox-badge inbox-badge-bot">Bot</span>`}
            ${sessionKindValue === 'closed' && html`
              <span
                class="inbox-badge inbox-badge-closed"
                title="24-hour window closed — wait for the customer to message"
                style="display:inline-flex;align-items:center;gap:4px;background:rgba(245,158,11,0.15);border:1px solid rgba(245,158,11,0.4);color:#fbbf24;border-radius:999px;padding:2px 9px;font-size:0.66rem;font-weight:600;line-height:1.2"
              >
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Closed
              </span>
            `}
          </div>
        `}
      </div>
    </div>
  `;
}
